import { Alert, Input, Modal, Typography } from 'antd'
import { useEffect, useState } from 'react'

import { useRoleActions } from '../hooks/useRoleActions'

interface Props {
  roleName?: string
  onCancel: () => void
  onDeleted: () => void
}

export function RoleDeleteModal({ roleName, onCancel, onDeleted }: Props) {
  const { deleteRole } = useRoleActions()
  const [confirmation, setConfirmation] = useState('')
  const [error, setError] = useState<string>()
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    setConfirmation('')
    setError(undefined)
    setSubmitting(false)
  }, [roleName])

  const handleDelete = async () => {
    if (!roleName || confirmation !== roleName || submitting) return

    setSubmitting(true)
    setError(undefined)
    try {
      await deleteRole(roleName)
      onDeleted()
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : 'Failed to delete role',
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      title="Delete role"
      open={Boolean(roleName)}
      okText="Delete"
      okButtonProps={{
        danger: true,
        disabled: confirmation !== roleName || submitting,
      }}
      confirmLoading={submitting}
      onOk={handleDelete}
      onCancel={onCancel}
      destroyOnHidden
    >
      <Typography.Paragraph>
        This permanently deletes the role. All users with this role will
        immediately be moved to Registered, changing their access. This cannot
        be undone.
      </Typography.Paragraph>
      <Typography.Paragraph>
        Type <strong>{roleName}</strong> to confirm.
      </Typography.Paragraph>
      {error && (
        <Alert
          type="error"
          showIcon
          message={error}
          style={{ marginBottom: 16 }}
        />
      )}
      <Input
        aria-label="Role name confirmation"
        value={confirmation}
        onChange={(event) => setConfirmation(event.target.value)}
        disabled={submitting}
        autoComplete="off"
      />
    </Modal>
  )
}
